import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';

import { useHistory, Link } from 'react-router-dom';
import { FaMoneyBill } from 'react-icons/fa';
import { MdSearch } from 'react-icons/md';
import { IPaginate } from 'shared/interfaces';
import { useToast } from 'contexts';
import { ModalHandles } from 'components/Modal';

import {
  getTitulos,
  deleteTitulo,
  baixarTitulo,
} from 'services/api/contasPagarCostureira';

import * as C from 'components';
import * as S from './styles';

import { IContasPagarCostureira } from '../interfaces';

export const ContasPagarCostureiraList: React.FC = () => {
  const { addToast } = useToast();
  const history = useHistory();
  const [titulos, setTitulos] = useState<IPaginate<IContasPagarCostureira>>();
  const [tituloAtual, setTituloAtual] = useState<IContasPagarCostureira>();
  const [loading, setLoading] = useState(false);
  const [term, setTerm] = useState('');
  const modalRef = useRef<ModalHandles>(null);
  const [modalType, setModalType] = useState('remove');

  const handleSearch = useCallback(
    ({ page, per_page }) => {
      setLoading(true);

      const params = {
        page,
        per_page,
        term,
      };

      getTitulos(params)
        .then((data) => {
          setTitulos(data);

          setLoading(false);
        })
        .catch((e) => {
          addToast({
            type: 'error',
            title: 'Ops, Erro',
            description: e.response?.data?.message,
          });

          setLoading(false);
        });
    },
    [addToast, term],
  );

  useEffect(() => {
    const params = {
      page: 1,
      per_page: 20,
    };

    handleSearch(params);
  }, [handleSearch]);

  const handleRemove = useCallback(async () => {
    try {
      await deleteTitulo({ id: tituloAtual!.id });

      if (!titulos) return;

      const newList = titulos.data.filter(
        (item) => item.id !== tituloAtual?.id,
      );
      setTitulos({ ...titulos, data: newList });

      modalRef.current?.closeModal();

      addToast({
        type: 'success',
        title: 'Sucesso',
        description: 'Registro removido com sucesso',
      });
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Ops, Erro',
        description: 'Ocorreu um erro ao remover o registro',
      });
    }
  }, [addToast, tituloAtual, titulos]);

  const handleChangeStatus = useCallback(async () => {
    try {
      await baixarTitulo(tituloAtual!.id);

      if (!titulos) return;

      const newList = titulos.data.map((item) => {
        if (item.id === tituloAtual?.id) {
          return { ...item, status: 'F' };
        }

        return item;
      });

      setTitulos({ ...titulos, data: newList });

      modalRef.current?.closeModal();

      addToast({
        type: 'success',
        title: 'Sucesso',
        description: 'Título baixado com sucesso',
      });
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Ops, Erro',
        description: 'Ocorreu um erro ao baixar o título',
      });
    }
  }, [addToast, tituloAtual, titulos]);

  const handleOpenModal = useCallback(
    (tituloSelected: IContasPagarCostureira, type: string): void => {
      setModalType(type);

      setTituloAtual(tituloSelected);

      modalRef.current?.openModal();
    },
    [],
  );

  const renderModal = useMemo(() => {
    if (modalType === 'remove') {
      return (
        <C.Modal ref={modalRef} options={{ width: '400px' }}>
          <C.ModalHeader>
            <C.ModalTitle>Você Confirma a EXCLUSÃO?</C.ModalTitle>
          </C.ModalHeader>
          <C.ModalBody>
            <p>O registro será removido completamente!</p>
          </C.ModalBody>
          <C.ModalFooter>
            <C.Position align="right">
              <C.Button
                variant="outline-secondary"
                onClick={() => modalRef.current?.closeModal()}
              >
                Cancelar
              </C.Button>
              <C.Button
                variant="primary"
                onClick={() => handleRemove()}
                style={{ marginLeft: '16px' }}
              >
                SIM, confirmo a exclusão
              </C.Button>
            </C.Position>
          </C.ModalFooter>
        </C.Modal>
      );
    }

    if (modalType === 'status') {
      return (
        <C.Modal ref={modalRef} options={{ width: '400px' }}>
          <C.ModalHeader>
            <C.ModalTitle>Confirma a baixa do título?</C.ModalTitle>
          </C.ModalHeader>
          <C.ModalBody>
            <p>O título sera baixado do contas a pagar!</p>
          </C.ModalBody>
          <C.ModalFooter>
            <C.Position align="right">
              <C.Button
                variant="outline-secondary"
                onClick={() => modalRef.current?.closeModal()}
              >
                Cancelar
              </C.Button>
              <C.Button
                variant="primary"
                onClick={() => handleChangeStatus()}
                style={{ marginLeft: '16px' }}
              >
                SIM, confirmo a baixa
              </C.Button>
            </C.Position>
          </C.ModalFooter>
        </C.Modal>
      );
    }

    return <></>;
  }, [handleChangeStatus, handleRemove, modalType]);

  return (
    <S.Container>
      <C.HeaderBackground>
        <C.HeaderPage>
          <C.HeaderInfo title="Títulos pagar produção" icon={FaMoneyBill} />

          <S.ContainerSearch>
            <C.InputSearch
              title="Digite aqui o que deseja pesquisar"
              icon={MdSearch}
              placeholder="Pesquisar"
              onChange={(e) => setTerm(e.target.value)}
              onKeyDown={(e) => {
                e.key === 'Enter'
                  ? handleSearch({
                      page: 1,
                      per_page: titulos?.per_page,
                    })
                  : {};
              }}
            />
          </S.ContainerSearch>

          <S.HeaderButtonContainer>
            <Link to="/contas-pagar-costureira/print">
              <S.IconPrint />
            </Link>

            <C.Can checkPermission="contas_pagar_costureira_create">
              <C.ButtonLink
                title="Adicionar Titulo"
                to="/contas-pagar-costureira/new"
              >
                Adicionar
              </C.ButtonLink>
            </C.Can>
          </S.HeaderButtonContainer>
        </C.HeaderPage>
      </C.HeaderBackground>

      <S.Panel>
        {loading ? (
          <C.Loader loading={loading} align="center" message="Carregando..." />
        ) : (
          <>
            <div className="tbl-content">
              <C.Table>
                <thead>
                  <tr>
                    <th className="text-center">Data Corte</th>
                    <th className="text-left">Costureira</th>
                    <th className="text-left">Forma</th>
                    <th className="text-left">Centro Custo</th>
                    <th className="text-left">Plano Contas</th>
                    <th className="text-center">Numero</th>
                    <th className="text-center">Corte</th>
                    <th className="text-center">Data Vencimento</th>
                    <th className="text-center">Valor Corte</th>
                    <th className="text-center">Pago</th>
                    <th className="text-center">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {titulos &&
                    titulos.data.map((item: IContasPagarCostureira) => (
                      <tr key={item.id}>
                        <td className="text-center">{item.data_compra}</td>
                        <td>{item.fornecedor?.nome}</td>
                        <td>{item.forma_pagamento?.nome}</td>
                        <td>{item.centro_custo?.nome}</td>
                        <td>{item.plano_contas?.nome}</td>
                        <td className="text-center">{item.numero_titulo}</td>
                        <td className="text-center">{item.numero_romaneio}</td>
                        <td className="text-center">{item.data_vencimento}</td>
                        <td className="text-right">{item.valor_romaneio}</td>
                        <td className="text-center">
                          <C.Status enable={String(item.status === 'F')} />
                        </td>
                        <td className="text-center">
                          <C.PopUpMenu>
                            <C.Can checkPermission="contas_pagar_costureira_update">
                              <button
                                type="button"
                                onClick={() =>
                                  history.push(
                                    `/contas-pagar-costureira/edit/${item.id}`,
                                  )
                                }
                              >
                                <S.IconEdit
                                  title="Editar"
                                  size={24}
                                  color="#707070"
                                />
                                Editar
                              </button>
                            </C.Can>

                            <C.Can checkPermission="contas_pagar_costureira_delete">
                              <button
                                type="button"
                                onClick={() => handleOpenModal(item, 'remove')}
                              >
                                <S.IconDelete
                                  title="Remover"
                                  size={24}
                                  color="#707070"
                                />
                                Remover
                              </button>
                            </C.Can>

                            <button
                              type="button"
                              onClick={() => handleOpenModal(item, 'status')}
                            >
                              <S.IconStatus
                                title="Titulo Pago"
                                size={24}
                                color="#707070"
                              />
                              Pago
                            </button>
                          </C.PopUpMenu>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </C.Table>
            </div>
          </>
        )}
        <S.PaginationContainer>
          {titulos && (
            <C.Pagination
              page={titulos?.page}
              total={titulos?.total}
              per_page={titulos?.per_page}
              last_page={titulos?.last_page}
              onSearch={handleSearch}
            />
          )}
        </S.PaginationContainer>
      </S.Panel>

      {renderModal}
    </S.Container>
  );
};
